'use client';

import {
  AnalyticsEventName,
  type ShopifyAnalyticsProduct,
  type ShopifyPageViewPayload,
  ShopifySalesChannel,
  getClientBrowserParameters,
  sendShopifyAnalytics,
} from '@shopify/hydrogen-react';
import type { CurrencyCode } from '@shopify/hydrogen-react/storefront-api-types';
import { useLogger } from 'next-axiom';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';
import { CURRENCY_CODE, LANGUAGE_CODE } from '../../lib/constants';
import { env } from '../../lib/env';

const shopId = env.NEXT_PUBLIC_SHOPIFY_CUSTOMER_SHOP_ID;
const currency = CURRENCY_CODE as CurrencyCode;
const defaultLanguage = LANGUAGE_CODE;

type SendPageViewPayload = {
  pageType?: string;
  products?: ShopifyAnalyticsProduct[];
  collectionHandle?: string;
  searchString?: string;
  totalValue?: number;
  cartId?: string;
};

type SendAddToCartPayload = {
  cartId: string;
  products?: ShopifyAnalyticsProduct[];
  totalValue?: ShopifyPageViewPayload['totalValue'];
};

export function useShopifyAnalytics() {
  const pathname = usePathname();

  const logger = useLogger().with({
    location: 'use-shopify-analytics.ts',
  });

  // Memoize the send function to prevent unnecessary re-renders
  // biome-ignore lint/correctness/useExhaustiveDependencies: Logger is not a dependency
  const sendPageView = useCallback(
    (
      eventName: keyof typeof AnalyticsEventName,
      payload?: SendPageViewPayload
    ) => {
      try {
        const browserParams = getClientBrowserParameters();
        return sendShopifyAnalytics({
          eventName,
          payload: {
            ...browserParams,
            analyticsAllowed: true,
            marketingAllowed: true,
            saleOfDataAllowed: true,
            hasUserConsent: true,
            shopifySalesChannel: ShopifySalesChannel.headless,
            shopId: `gid://shopify/Shop/${shopId}`,
            currency,
            acceptedLanguage: defaultLanguage,
            ...payload,
          },
        });
      } catch (error) {
        logger.error('Shopify analytics error', { error });
        return Promise.resolve();
      }
    },
    []
  );

  const sendAddToCart = useCallback(
    ({ cartId, totalValue, products }: SendAddToCartPayload) =>
      sendPageView(AnalyticsEventName.ADD_TO_CART, {
        cartId,
        totalValue,
        products,
      }),
    [sendPageView]
  );

  return {
    sendPageView,
    sendAddToCart,
    pathname,
  };
}
